import type { Hub } from './display/Hub';
import {
  type ActivationEvent,
  RADIAL_KEYBOARD_BLUR_EVENT,
  RADIAL_KEYBOARD_FOCUS_EVENT,
  type Radial,
} from './display/Radial';
import { BUTTON_RADIUS } from './display/const';

export type TabOrderEntry = {
  obj: createjs.DisplayObject;
  title: string;
  disabled: boolean;
};

type AccessibleDisplayObject = createjs.DisplayObject & {
  boundsToGlobal?: () => createjs.Rectangle;
  zimTabTag?: HTMLElement;
};

type AccessibilityFrame = createjs.EventDispatcher & {
  canvas: HTMLCanvasElement;
  stage: createjs.Stage;
  retina: false;
  scale: number;
  shiftKey: boolean;
  shim: false;
  x: number;
  y: number;
};

type ZimAccessibility = {
  tabIndex: number;
  tabOrder: TabOrderEntry[];
  resize(): void;
  dispose(): void;
};

type FocusHandlers = {
  obj: createjs.DisplayObject;
  onBlur(event: FocusEvent): void;
  onFocus(): void;
  onKeyDown(event: KeyboardEvent): void;
};

const BASE_CENTRALIZE_ANIMATION_MS = 250;
const TAB_ORDER_REBUILD_DELAY_MS = 50;
let canvasId = 0;

export function getAccessibleName(radial: Pick<Radial, 'ariaLabel'>): string {
  return radial.ariaLabel;
}

export function getAccessibleTitle(
  radial: Pick<
    Radial,
    'ariaLabel' | 'enabled' | 'inFocus' | 'underConstruction'
  >,
  position: 'center' | 'child' | 'breadcrumb',
): string {
  const name = getAccessibleName(radial);

  if (radial.underConstruction) {
    return `${name}, under construction`;
  }

  if (!radial.enabled || !radial.inFocus) {
    return `${name}, disabled`;
  }

  if (position === 'center') {
    return `${name}, go back`;
  }

  if (position === 'breadcrumb') {
    return `${name}, breadcrumb`;
  }

  return name;
}

export function isAccessibilityActionAvailable(
  radial: Pick<Radial, 'display' | 'enabled' | 'inFocus' | 'underConstruction'>,
): boolean {
  return (
    radial.display &&
    radial.enabled &&
    radial.inFocus &&
    !radial.underConstruction
  );
}

function createTabOrderEntry(
  radial: Radial,
  position: 'center' | 'child' | 'breadcrumb',
): TabOrderEntry {
  return {
    obj: radial.button,
    title: getAccessibleTitle(radial, position),
    disabled: !isAccessibilityActionAvailable(radial),
  };
}

export function buildTabOrder(hub: Hub): TabOrderEntry[] {
  const central = hub.centralRadial as Radial | null;
  if (!central) return [];

  const tabOrder: TabOrderEntry[] = [];

  if (central.radialParent && central.display) {
    tabOrder.push(createTabOrderEntry(central, 'center'));
  }

  const children = [...central.radialChildren]
    .filter((radial) => radial.display && !radial.hidden)
    .sort((a, b) => a.angle - b.angle);

  for (const child of children) {
    tabOrder.push(createTabOrderEntry(child, 'child'));
  }

  for (const ancestor of central.radialAncestors) {
    if (ancestor.display && !ancestor.hidden) {
      tabOrder.push(createTabOrderEntry(ancestor, 'breadcrumb'));
    }
  }

  return tabOrder;
}

export async function initAccessibility(
  hub: Hub,
  stage: createjs.Stage,
): Promise<(() => void) | null> {
  const initialTabOrder = buildTabOrder(hub);
  if (initialTabOrder.length === 0) return null;

  // zimjs imports its own CreateJS fork and assigns it to window.createjs.
  // Preserve the runtime that created this stage and restore it immediately
  // so the existing hub does not mix objects from two CreateJS namespaces.
  const appCreatejs = createjs;
  const zim = await loadZimPreservingCreatejs(appCreatejs);

  const {
    frame,
    dispose: disposeFrame,
    updateGeometry,
  } = createAccessibilityFrame(stage);
  const adaptedObjects = new Map<AccessibleDisplayObject, unknown>();
  const focusHandlers = new Map<HTMLElement, FocusHandlers>();
  const AccessibilityCtor = zim.Accessibility as unknown as new (config: {
    appName: string;
    frame: AccessibilityFrame;
    tabOrder: TabOrderEntry[];
  }) => ZimAccessibility;

  let accessibility: ZimAccessibility;
  try {
    prepareTabOrder(initialTabOrder, adaptedObjects);
    accessibility = new AccessibilityCtor({
      appName: 'FIM Hub',
      frame,
      tabOrder: initialTabOrder,
    });
    applyDisabledState(initialTabOrder);
    disableBoundaryTabStops(stage.canvas as HTMLCanvasElement, initialTabOrder);
    applyFocusHandlers(initialTabOrder, focusHandlers, accessibility);
  } catch (error) {
    clearFocusHandlers(focusHandlers);
    disposeFrame();
    restoreAdaptedObjects(adaptedObjects);
    throw error;
  }

  let rebuildTimer: ReturnType<typeof setTimeout> | null = null;

  function rebuild(): void {
    const focusedObject = focusHandlers.get(
      document.activeElement as HTMLElement,
    )?.obj;
    const tabOrder = buildTabOrder(hub);
    prepareTabOrder(tabOrder, adaptedObjects);
    clearFocusHandlers(focusHandlers);
    accessibility.tabOrder = tabOrder;
    applyDisabledState(tabOrder);
    disableBoundaryTabStops(stage.canvas as HTMLCanvasElement, tabOrder);
    updateGeometry();
    accessibility.resize();
    applyFocusHandlers(tabOrder, focusHandlers, accessibility);

    if (focusedObject) {
      const focusTarget = tabOrder.some(({ obj }) => obj === focusedObject)
        ? focusedObject
        : tabOrder.find(({ disabled }) => !disabled)?.obj;
      if (focusTarget) {
        (focusTarget as AccessibleDisplayObject).zimTabTag?.focus();
      } else {
        accessibility.tabIndex = -1;
      }
    }
  }

  function scheduleRebuild(): void {
    if (rebuildTimer) clearTimeout(rebuildTimer);
    const animationMs =
      hub.animationDurationFactor * BASE_CENTRALIZE_ANIMATION_MS;
    rebuildTimer = setTimeout(
      rebuild,
      animationMs + TAB_ORDER_REBUILD_DELAY_MS,
    );
  }

  const unsubscribeCentralize = hub.subscribeCentralize(scheduleRebuild);

  function onResize(): void {
    updateGeometry();
    accessibility.resize();
  }

  window.addEventListener('resize', onResize);
  scheduleRebuild();

  return () => {
    if (rebuildTimer) clearTimeout(rebuildTimer);
    rebuildTimer = null;
    window.removeEventListener('resize', onResize);
    unsubscribeCentralize();
    clearFocusHandlers(focusHandlers);
    accessibility.dispose();
    disposeFrame();
    restoreAdaptedObjects(adaptedObjects);
  };
}

async function loadZimPreservingCreatejs(
  appCreatejs: typeof createjs,
): Promise<typeof import('zimjs')> {
  try {
    return await import('zimjs');
  } finally {
    (
      window as Window & {
        createjs: typeof createjs;
      }
    ).createjs = appCreatejs;
  }
}

function createAccessibilityFrame(stage: createjs.Stage): {
  frame: AccessibilityFrame;
  dispose(): void;
  updateGeometry(): void;
} {
  const canvas = stage.canvas as HTMLCanvasElement;
  if (!canvas.id) canvas.id = `fim-hub-canvas-${++canvasId}`;

  const frame = new createjs.EventDispatcher() as AccessibilityFrame;
  frame.canvas = canvas;
  frame.stage = stage;
  frame.retina = false;
  frame.scale = 1;
  frame.shiftKey = false;
  frame.shim = false;
  frame.x = 0;
  frame.y = 0;

  function updateGeometry(): void {
    const bounds = canvas.getBoundingClientRect();
    const parentBounds = canvas.parentElement?.getBoundingClientRect();
    frame.x = parentBounds
      ? bounds.left - parentBounds.left
      : bounds.left + window.scrollX;
    frame.y = parentBounds
      ? bounds.top - parentBounds.top
      : bounds.top + window.scrollY;
    frame.scale = canvas.width ? bounds.width / canvas.width : 1;
  }

  updateGeometry();

  return {
    frame,
    updateGeometry,
    dispose: () => {
      frame.removeAllEventListeners();
    },
  };
}

function prepareTabOrder(
  tabOrder: TabOrderEntry[],
  adaptedObjects: Map<AccessibleDisplayObject, unknown>,
): void {
  for (const { obj } of tabOrder) {
    const accessibleObject = obj as AccessibleDisplayObject;
    if (adaptedObjects.has(accessibleObject)) continue;

    const bounds = getDisplayObjectBounds(accessibleObject);
    adaptedObjects.set(accessibleObject, accessibleObject.boundsToGlobal);
    accessibleObject.boundsToGlobal = () =>
      getBoundsToGlobal(accessibleObject, bounds);
  }
}

function getBoundsToGlobal(
  obj: createjs.DisplayObject,
  bounds: createjs.Rectangle,
): createjs.Rectangle {
  const points = [
    obj.localToGlobal(bounds.x, bounds.y),
    obj.localToGlobal(bounds.x + bounds.width, bounds.y),
    obj.localToGlobal(bounds.x + bounds.width, bounds.y + bounds.height),
    obj.localToGlobal(bounds.x, bounds.y + bounds.height),
  ];
  const xValues = points.map((point) => point.x);
  const yValues = points.map((point) => point.y);
  const left = Math.min(...xValues);
  const top = Math.min(...yValues);

  return new createjs.Rectangle(
    left,
    top,
    Math.max(...xValues) - left,
    Math.max(...yValues) - top,
  );
}

function getDisplayObjectBounds(
  obj: createjs.DisplayObject,
): createjs.Rectangle {
  return (
    (obj.getBounds as () => createjs.Rectangle | null)() ??
    new createjs.Rectangle(
      -BUTTON_RADIUS,
      -BUTTON_RADIUS,
      BUTTON_RADIUS * 2,
      BUTTON_RADIUS * 2,
    )
  );
}

function applyDisabledState(tabOrder: TabOrderEntry[]): void {
  for (const entry of tabOrder) {
    const tag = (entry.obj as AccessibleDisplayObject).zimTabTag;
    if (tag) tag.setAttribute('aria-disabled', String(entry.disabled));
  }
}

function disableBoundaryTabStops(
  canvas: HTMLCanvasElement,
  tabOrder: TabOrderEntry[],
): void {
  // ZIM always creates offscreen boundary tags for its internal Tab loop.
  // This hub uses the generated node tags in the browser's natural tab order,
  // so the helper tags must not add invisible stops before and after the canvas.
  const nodeTags = new Set(
    tabOrder
      .map(({ obj }) => (obj as AccessibleDisplayObject).zimTabTag)
      .filter((tag): tag is HTMLElement => !!tag),
  );

  for (const element of canvas.parentElement?.children ?? []) {
    if (
      element instanceof HTMLElement &&
      element !== canvas &&
      element.id.startsWith(canvas.id) &&
      !nodeTags.has(element)
    ) {
      element.tabIndex = -1;
      element.setAttribute('aria-hidden', 'true');
    }
  }
}

function applyFocusHandlers(
  tabOrder: TabOrderEntry[],
  focusHandlers: Map<HTMLElement, FocusHandlers>,
  accessibility: ZimAccessibility,
): void {
  for (const entry of tabOrder) {
    const tag = (entry.obj as AccessibleDisplayObject).zimTabTag;
    if (!tag) continue;

    function onFocus(): void {
      entry.obj.dispatchEvent(RADIAL_KEYBOARD_FOCUS_EVENT);
    }
    function onBlur(event: FocusEvent): void {
      entry.obj.dispatchEvent(RADIAL_KEYBOARD_BLUR_EVENT);
      if (
        !(event.relatedTarget instanceof HTMLElement) ||
        !focusHandlers.has(event.relatedTarget)
      ) {
        accessibility.tabIndex = -1;
      }
    }
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.key !== 'Enter' && event.key !== ' ') || event.repeat) return;

      event.preventDefault();
      const clickEvent = new createjs.Event(
        'click',
        false,
        false,
      ) as ActivationEvent;
      clickEvent.fromKeyboard = true;
      entry.obj.dispatchEvent(clickEvent);
    }
    tag.addEventListener('focus', onFocus);
    tag.addEventListener('blur', onBlur);
    tag.addEventListener('keydown', onKeyDown);
    focusHandlers.set(tag, {
      obj: entry.obj,
      onBlur,
      onFocus,
      onKeyDown,
    });
  }
}

function clearFocusHandlers(
  focusHandlers: Map<HTMLElement, FocusHandlers>,
): void {
  for (const [tag, handlers] of focusHandlers) {
    if (document.activeElement === tag) {
      handlers.obj.dispatchEvent(RADIAL_KEYBOARD_BLUR_EVENT);
    }
    tag.removeEventListener('focus', handlers.onFocus);
    tag.removeEventListener('blur', handlers.onBlur);
    tag.removeEventListener('keydown', handlers.onKeyDown);
  }
  focusHandlers.clear();
}

function restoreAdaptedObjects(
  adaptedObjects: Map<AccessibleDisplayObject, unknown>,
): void {
  for (const [obj, previousBoundsToGlobal] of adaptedObjects) {
    if (previousBoundsToGlobal === undefined) {
      delete obj.boundsToGlobal;
    } else {
      obj.boundsToGlobal = previousBoundsToGlobal as () => createjs.Rectangle;
    }
  }
}
